import React, { useMemo } from "react";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { EffectiveSplitDescriptionParams } from "@common/helpers/split-aware-description.helper";
import { RuleGroupType } from "@common/types/transactionRule";
import { KICK_GROUP_ICON } from "@common/constants/rules";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import { BulkUpdateParams } from "@components/transactions/TransactionsBulkActions/lib";
import { Button } from "@design-system/Button/Button";
import { ActionsProvider, useActionsContext } from "./ActionsContext";
import { ActionsTable } from "./ActionsTable";
import { ActionsCardFooter } from "./ActionsCardFooter";
import { useConfirmActions } from "./useConfirmActions";
import { useRuleMatchClassification } from "./useDuplicateRuleCheck";
import { ConflictingRulesModal } from "./ConflictingRulesModal";
import {
    CREATE_RULE_REQUIRES_ACTION_VALUE_TOOLTIP,
    CREATE_RULE_REQUIRES_CONDITION_TOOLTIP,
    CREATE_RULE_REQUIRES_GROUP_TOOLTIP,
    isCreateRuleInvalid,
    isCreateRuleMissingActionValue,
    isCreateRuleMissingGroup,
} from "./createRuleValidation";
import styles from "./ActionsCard.module.scss";

interface ActionsCardProps {
    valueToAssign: BulkUpdateParams;
    displayedChangedEntityName?: string;
    filters: TransactionFiltersDto;
    sourceSplitParams: EffectiveSplitDescriptionParams;
    newValuesMap: Record<number, BulkUpdateParams["value"]>;
    totalTransactions: number;
    similarToFallbackLabel?: string;
    onDone: () => void;
}

export const ActionsCard: React.FC<ActionsCardProps> = ({
    valueToAssign,
    displayedChangedEntityName,
    filters,
    sourceSplitParams,
    newValuesMap,
    totalTransactions,
    similarToFallbackLabel,
    onDone,
}) => {
    const ruleGroupsList = useWorkspaceRuleGroupsList();

    const defaultGroupId = useMemo(() => {
        const candidates = (ruleGroupsList.data ?? []).filter(
            (g) =>
                g.type === RuleGroupType.TRANSACTION &&
                g.icon !== KICK_GROUP_ICON,
        );
        return (
            candidates.find((g) => g.isDefault)?.id ?? candidates[0]?.id ?? ""
        );
    }, [ruleGroupsList.data]);

    // Wait for the rule groups list before mounting the provider so the
    // default group id is available on first render.
    if (!ruleGroupsList.data) {
        return null;
    }

    return (
        <ActionsProvider
            valueToAssign={valueToAssign}
            filters={filters}
            sourceSplitParams={sourceSplitParams}
            newValuesMap={newValuesMap}
            defaultRuleGroupId={defaultGroupId}
            totalTransactions={totalTransactions}
            similarToFallbackLabel={similarToFallbackLabel}
        >
            <ActionsCardInner
                displayedChangedEntityName={displayedChangedEntityName}
                totalTransactions={totalTransactions}
                filters={filters}
                onDone={onDone}
            />
        </ActionsProvider>
    );
};

interface InnerProps {
    displayedChangedEntityName?: string;
    totalTransactions: number;
    filters: TransactionFiltersDto;
    onDone: () => void;
}

const ActionsCardInner: React.FC<InnerProps> = ({
    displayedChangedEntityName,
    totalTransactions,
    filters,
    onDone,
}) => {
    const {
        appendEmptyRow,
        rows,
        isRuleAuthoringAvailable,
        valueToAssign,
        ruleGroupId,
    } = useActionsContext();

    // Side-effect hook: classifies each `createRule` row against rules in the
    // selected group and writes `ruleMatch` onto the row record. Drives both
    // the silent skip/merge paths and the conflict modal.
    useRuleMatchClassification();

    const {
        confirm,
        isSubmitting,
        conflictModalState,
        resolveConflicts,
        cancelConflicts,
        clearClosedConflicts,
    } = useConfirmActions({
        filters,
        displayedChangedEntityName,
        totalTransactions,
        onDone,
    });

    // Confirm is disabled when a toggled-on row has no backend-ready
    // conditions or no picked target value. Duplicate rules are handled via
    // the conflict modal. When rule authoring is unavailable (no seedable
    // defaults and no user-added conditions), the Create rule column is
    // hidden, so the residual `createRule: true` on default rows must not
    // block confirmation.
    const invalidRow = isRuleAuthoringAvailable
        ? rows.find((row) => isCreateRuleInvalid(valueToAssign.update, row))
        : undefined;
    const isMissingRuleGroup =
        isRuleAuthoringAvailable &&
        isCreateRuleMissingGroup({ rows, ruleGroupId });
    const hasInvalidRuleRow = invalidRow !== undefined || isMissingRuleGroup;
    let confirmDisabledTooltip: string | undefined;
    if (invalidRow) {
        confirmDisabledTooltip = isCreateRuleMissingActionValue(
            valueToAssign.update,
            invalidRow,
        )
            ? CREATE_RULE_REQUIRES_ACTION_VALUE_TOOLTIP
            : CREATE_RULE_REQUIRES_CONDITION_TOOLTIP;
    } else if (isMissingRuleGroup) {
        confirmDisabledTooltip = CREATE_RULE_REQUIRES_GROUP_TOOLTIP;
    }

    return (
        <>
            <section className={styles.card}>
                <header className={styles.cardHeader}>
                    <span className={styles.cardTitle}>Actions</span>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={appendEmptyRow}
                        type="button"
                    >
                        Add action
                    </Button>
                </header>
                <ActionsTable />
                <ActionsCardFooter
                    onConfirm={confirm}
                    isSubmitting={isSubmitting}
                    confirmDisabled={hasInvalidRuleRow}
                    confirmDisabledTooltip={confirmDisabledTooltip}
                />
            </section>

            <ConflictingRulesModal
                isOpen={conflictModalState.isOpen}
                conflicts={conflictModalState.conflicts}
                onBack={cancelConflicts}
                onConfirm={resolveConflicts}
                onExited={clearClosedConflicts}
            />
        </>
    );
};
